import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Box,
  Divider,
  IconButton,
  Tooltip,
  Link,
  Chip,
  Paper,
  Card,
  CardContent,
  CardMedia,
  CardHeader,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SecurityIcon from "@mui/icons-material/Security";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import InfoIcon from "@mui/icons-material/Info";
import { modelOptions } from "./TestComponents2"; // Import the model data

export const AIModelInfoLink = () => {
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <Tooltip title="Klicka för mer information om AI-modellerna">
        <Link
          component="button"
          variant="body2"
          onClick={handleOpen}
          sx={{
            textDecoration: "none",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            color: "primary.main",
            fontWeight: 500,
            transition: "all 0.2s ease",
            "&:hover": {
              color: "primary.dark",
              transform: "translateY(-1px)",
            },
          }}
        >
          Läs mer här
          <QuestionMarkIcon
            sx={{
              fontSize: 16,
              ml: 0.5,
              verticalAlign: "middle",
            }}
          />
        </Link>
      </Tooltip>
      <AIModelInfoDialog open={open} onClose={handleClose} />
    </>
  );
};

export const AIModelInfoDialog = ({ open, onClose }) => {
  const [selectedCategory, setSelectedCategory] = useState("all");

  const filterCategories = [
    { value: "all", label: "Alla modeller" },
    { value: "eu", label: "EU-servrar" },
    //{ value: "multimodal", label: "Multimodala" },
  ];

  const filteredModels =
    selectedCategory === "all"
      ? modelOptions
      : selectedCategory === "eu"
        ? modelOptions.filter((m) => m.badge === "EU-servrar")
        : selectedCategory === "multimodal"
          ? modelOptions.filter((m) =>
              m.description.toLowerCase().includes("multimodal"),
            )
          : modelOptions;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      aria-labelledby="ai-model-info-dialog-title"
      PaperProps={{
        elevation: 5,
        sx: {
          borderRadius: 2,
          position: "relative",
        },
      }}
    >
      <IconButton
        edge="end"
        sx={{ position: "absolute", top: 5, right: 15 }}
        onClick={onClose}
        size="medium"
        aria-label="close"
      >
        <CloseIcon size="medium" />
      </IconButton>

      <DialogContent sx={{ p: 4 }}>
        <Box
          sx={{
            mb: 2,
            p: 3,
            borderRadius: 2,
            bgcolor: "rgba(25, 118, 210, 0.08)",
            border: "1px solid rgba(25, 118, 210, 0.2)",
          }}
        >
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            <InfoIcon sx={{ mr: 1, verticalAlign: "middle", fontSize: 24 }} />
            Om våra AI-modeller
          </Typography>
          <Typography variant="body1" sx={{}}>
            När du genererar innehåll hos oss kan du välja mellan flera olika
            AI-modeller. Varje modell har sina unika styrkor och rekommenderade
            användningsområden. Vi uppdaterar regelbundet vårt utbud för att
            erbjuda dig de senaste och mest kapabla AI-modellerna för dina
            behov, medan vi ständigt tränar vår egen modell.
          </Typography>
        </Box>
        <Box sx={{ mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm="auto" sx={{ display: "none" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Filtrera efter:
              </Typography>
            </Grid>
            <Grid item xs={12} sm>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {filterCategories.map((category) => (
                  <Chip
                    key={category.value}
                    label={category.label}
                    onClick={() => setSelectedCategory(category.value)}
                    color={
                      selectedCategory === category.value
                        ? "primary"
                        : "default"
                    }
                    variant={
                      selectedCategory === category.value
                        ? "filled"
                        : "outlined"
                    }
                    sx={{
                      fontWeight:
                        selectedCategory === category.value ? 600 : 400,
                      "&:hover": { opacity: 0.9 },
                    }}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </Box>
        <Grid container spacing={3}>
          {filteredModels.map((model) => (
            <Grid item xs={12} sm={6} md={4} key={model.value}>
              <Card
                elevation={2}
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 3,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    // transform: "translateY(-4px)",
                    // boxShadow: 8,
                  },
                  position: "relative",
                  overflow: "visible",
                }}
              >
                {model.badge && (
                  <Chip
                    icon={<SecurityIcon />}
                    label={model.badge}
                    color="success"
                    sx={{
                      position: "absolute",
                      top: -12,
                      right: 16,
                      fontWeight: 600,
                      zIndex: 1,
                      border: "2px solid white",
                    }}
                  />
                )}

                <CardHeader
                  avatar={
                    <Avatar
                      src={model.img}
                      alt={model.alt}
                      sx={{
                        width: 48,
                        height: 48,
                        p: model.value === "Studera.AI" ? 0.5 : 0,
                        bgcolor: "#f0f3ff",
                      }}
                    />
                  }
                  title={
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {model.label}
                    </Typography>
                  }
                  sx={{ pb: 0 }}
                />

                <CardContent
                  sx={{
                    pt: 1,
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ mb: 2, color: "text.secondary" }}
                  >
                    {model.description}
                  </Typography>

                  {/* Strengths section */}
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, mb: 1, color: "primary.main" }}
                  >
                    Styrkor:
                  </Typography>
                  <List dense disablePadding sx={{ mb: 2 }}>
                    {model.strengths.map((strength, index) => (
                      <ListItem key={index} disableGutters sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <CheckCircleOutlineIcon
                            color="success"
                            fontSize="small"
                          />
                        </ListItemIcon>
                        <ListItemText primary={strength} />
                      </ListItem>
                    ))}
                  </List>

                  {/* Use case section */}
                  <Box sx={{ mt: "auto" }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mb: 1, color: "primary.main" }}
                    >
                      Användningsområde:
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {model.useCase}
                    </Typography>

                    {/* Security note for Mistral */}
                    {model.securityNote && (
                      <Box
                        sx={{
                          mt: 1,
                          p: 1.5,
                          borderRadius: 1,
                          bgcolor: "success.light",
                          color: "success.contrastText",
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1,
                        }}
                      >
                        <SecurityIcon fontSize="small" sx={{ mt: 0.3 }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {model.securityNote}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

// Modified Tooltip component that uses our custom dialog
export const AIModelInfoTooltip = ({ children }) => {
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <Tooltip
        title={
          <React.Fragment>
            <Typography variant="body2">
              Vi stödjer över 10 AI-modeller och lägger till nya löpande. De är
              olika bra på olika saker.{" "}
              <Link
                component="button"
                onClick={(e) => {
                  e.preventDefault();
                  handleOpen();
                }}
                sx={{
                  color: "inherit",
                  textDecoration: "underline",
                  cursor: "pointer",
                  fontWeight: "bold",
                }}
              >
                Läs mer här
              </Link>
            </Typography>
          </React.Fragment>
        }
        arrow
        placement="top"
        sx={{
          bgcolor: "rgba(0,0,0,0.8)",
          p: 1,
          borderRadius: 1,
        }}
      >
        {children}
      </Tooltip>
      <AIModelInfoDialog open={open} onClose={handleClose} />
    </>
  );
};
